/**
 * rental_rows.ts — the rentals rows and reducers as the client reads them (the interiors programme, slice R). One
 * place for the structural casts (THE TS2589 TRAP: never let the generated `conn.db` type flow through a module).
 */
import type { Identity } from 'spacetimedb';
import { atlas } from '../../state.js';
import { STRUCTURE_LABELS } from '@shared/types/structures.js';
import { structureDisplayName } from '../../lib/structure_access.js';
import type { RentalUnitRow, RentalLeaseRow, RentalReceiptRow } from '../../lib/rental_client.js';

export interface StructureRowLite { structureId: bigint; owner: Identity; structureType: string; posX: number; posZ: number; yaw: number }
export interface RentalContainerRow { containerId: bigint; leaseId: bigint; structureId: bigint; key: string; kind: string; capacity: number }
export interface RentalStoredRow { entryId: bigint; containerId: bigint; leaseId: bigint; kind: string; itemKey: string; name: string; quality: number; qty: number }
interface Table<T> { iter(): Iterable<T> }
interface Db {
  rentalUnit?: Table<RentalUnitRow>; rentalLease?: Table<RentalLeaseRow>; rentalReceipt?: Table<RentalReceiptRow>;
  rentalContainer?: Table<RentalContainerRow>; rentalStored?: Table<RentalStoredRow>;
  structure?: { structureId: { find(id: bigint): StructureRowLite | undefined }; iter(): Iterable<StructureRowLite> };
  structureMeta?: { structureId: { find(id: bigint): { name: string } | undefined } };
  structureLayout?: { structureId: { find(id: bigint): { layoutRevision: number; digest: string } | undefined } };
}
export interface RentalReducers {
  listRentalUnit(a: {
    structureId: bigint; unitId: string; rentPerDay: number; notes: string; digest: string; kind: string; floor: number;
    x0: number; z0: number; x1: number; z1: number; tx: number; tz: number; ax: number; az: number; ox: number; oz: number; keys: string[];
  }): Promise<void>;
  unlistRentalUnit(a: { structureId: bigint; unitId: string }): Promise<void>;
  evictTenant(a: { leaseId: bigint }): Promise<void>;
  rentUnit(a: { structureId: bigint; unitId: string; days: number; expectRentPerDay: number }): Promise<void>;
  payRent(a: { leaseId: bigint; days: number }): Promise<void>;
  leaveRental(a: { leaseId: bigint }): Promise<void>;
  openRentalStorage(a: { leaseId: bigint; key: string }): Promise<void>;
  storeRentalItem(a: { containerId: bigint; playerItemId: bigint }): Promise<void>;
  storeRentalStack(a: { containerId: bigint; stackId: bigint; qty: number }): Promise<void>;
  takeRentalItem(a: { entryId: bigint }): Promise<void>;
  takeRentalStack(a: { entryId: bigint; qty: number }): Promise<void>;
  createWaypoint?(a: { name: string; posX: number; posZ: number }): Promise<void>;
}

export function rentalDb(): Db { const conn = atlas.conn; return conn ? (conn.db as unknown as Db) : ({} as Db); }
export function rentalReducers(): RentalReducers | null { const conn = atlas.conn; return conn ? (conn.reducers as unknown as RentalReducers) : null; }
export function me(): Identity | null { return atlas.conn?.identity ?? null; }
const safe = <T>(t: Table<T> | undefined): T[] => { try { return t ? [...t.iter()] : []; } catch { return []; } };

export const allUnits = (): RentalUnitRow[] => safe(rentalDb().rentalUnit);
export const allLeases = (): RentalLeaseRow[] => safe(rentalDb().rentalLease);
export const allReceipts = (): RentalReceiptRow[] => safe(rentalDb().rentalReceipt);
export const unitsOfStructure = (structureId: bigint): RentalUnitRow[] => allUnits().filter((u) => u.structureId === structureId).sort((a, b) => (a.unitId < b.unitId ? -1 : 1));
export const liveLeaseOfUnit = (unitRowId: bigint): RentalLeaseRow | null => allLeases().find((l) => l.unitRowId === unitRowId && l.state === 'live') ?? null;
export const liveLeasesOfStructure = (structureId: bigint): RentalLeaseRow[] => allLeases().filter((l) => l.structureId === structureId && l.state === 'live');
export const isMine = (l: RentalLeaseRow): boolean => { const id = me(); return !!id && l.tenant.isEqual(id as never); };
export function structureRow(structureId: bigint): StructureRowLite | null {
  try { return rentalDb().structure?.structureId.find(structureId) ?? null; } catch { return null; }
}
export function structureName(structureId: bigint): string {
  const s = structureRow(structureId);
  let meta: string | null = null;
  try { meta = rentalDb().structureMeta?.structureId.find(structureId)?.name ?? null; } catch { meta = null; }
  const label = s ? (STRUCTURE_LABELS[s.structureType] ?? s.structureType.replace(/_/g, ' ')) : `Structure #${structureId}`;
  return structureDisplayName(meta, label);
}
/** The tenant's container on a piece of their room, and what it holds (rows only the tenant is ever handed). */
export function tenantContainer(leaseId: bigint, key: string): RentalContainerRow | null {
  return safe(rentalDb().rentalContainer).find((c) => c.leaseId === leaseId && c.key === key) ?? null;
}
export const tenantEntries = (containerId: bigint): RentalStoredRow[] => safe(rentalDb().rentalStored).filter((e) => e.containerId === containerId);
/** The structure's layout pin as it stands NOW (the row, not the visit's snapshot of it). */
export function layoutPin(structureId: bigint): { layoutRevision: number; digest: string } | null {
  try { return rentalDb().structureLayout?.structureId.find(structureId) ?? null; } catch { return null; }
}
